import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { VideoPlayer } from '../components/video/VideoPlayer';
import { ConversationPrompt } from '../components/ConversationPrompt';
import { PromptButton } from '../components/PromptButton';
import { ReportModal } from '../components/ReportModal';
import { CallControls } from '../components/CallControls';
import { RatingModal } from '../components/RatingModal';
import { UpgradePromptModal } from '../components/UpgradePromptModal';
import WebRTCService from '../services/webrtc.service';
import PromptsService, { ConversationPrompt as PromptType } from '../services/prompts.service';
import ReportsService from '../services/reports.service';
import SessionsService from '../services/sessions.service';
import ConnectionQualityService, {
  ConnectionStats,
  ConnectionQuality,
} from '../services/connection-quality.service';
import { usersService } from '../services/users.service';

export const VideoCallPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, user } = useAuth();

  // Auto-start matching when navigating to video page
  const shouldStartMatching = location.state?.startMatching || false;

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [connectionState, setConnectionState] = useState<string>('initializing');
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [connectionStats, setConnectionStats] = useState<ConnectionStats | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState<PromptType | null>(null);
  const [promptEngaged, setPromptEngaged] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'daily_limit' | 'time_limit' | 'premium_feature'>('time_limit');
  const [sessionsUsedToday, setSessionsUsedToday] = useState<number>(0);
  const [dailyLimit, setDailyLimit] = useState<number>(3);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [sessionTime, setSessionTime] = useState(0); // in seconds
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);

  const webrtcService = useRef<WebRTCService | null>(null);
  const qualityService = useRef(new ConnectionQualityService());
  const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Session limits
  const FREE_SESSION_LIMIT = 10 * 60; // 10 minutes in seconds
  const isPremium = user?.isPremium || false;

  useEffect(() => {
    if (!accessToken) {
      navigate('/login');
      return;
    }

    initializeCall();

    return () => {
      webrtcService.current?.disconnect();
    };
  }, [accessToken, navigate]);

  // Auto-start matching if coming from mood selection
  useEffect(() => {
    if (shouldStartMatching && connectionState === 'ready' && !isSearching) {
      startSearching();
    }
  }, [shouldStartMatching, connectionState, isSearching]);

  // Fetch conversation prompt when connected
  useEffect(() => {
    if (connectionState === 'connected' && !currentPrompt && accessToken) {
      fetchPrompt();
    }
  }, [connectionState, currentPrompt, accessToken]);

  // Monitor connection quality when connected
  useEffect(() => {
    if (connectionState !== 'connected') {
      setConnectionStats(null);
      qualityService.current.reset();
      return;
    }

    const interval = setInterval(async () => {
      const peerConnection = webrtcService.current?.getPeerConnection();
      if (peerConnection) {
        try {
          const stats = await qualityService.current.getConnectionStats(peerConnection);
          setConnectionStats(stats);
        } catch (err) {
          console.error('Failed to get connection stats:', err);
        }
      }
    }, 2000); // Update every 2 seconds

    return () => clearInterval(interval);
  }, [connectionState]);

  // Session timer
  useEffect(() => {
    if (connectionState === 'connected' && !sessionStartTime) {
      // Start session timer
      setSessionStartTime(Date.now());
      setSessionTime(0);
    } else if (connectionState !== 'connected' && sessionStartTime) {
      // Reset session timer
      setSessionStartTime(null);
      setSessionTime(0);
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    }

    if (connectionState === 'connected' && sessionStartTime) {
      // Update session timer every second
      sessionTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
        setSessionTime(elapsed);
      }, 1000);

      return () => {
        if (sessionTimerRef.current) {
          clearInterval(sessionTimerRef.current);
          sessionTimerRef.current = null;
        }
      };
    }
  }, [connectionState, sessionStartTime]);

  // Time limit enforcement for free users
  useEffect(() => {
    if (!isPremium && connectionState === 'connected' && sessionTime >= FREE_SESSION_LIMIT) {
      // Auto-disconnect at limit
      setUpgradeReason('time_limit');
      setIsUpgradeModalOpen(true);
      endCall();
    }
  }, [sessionTime, isPremium, connectionState]);

  const fetchPrompt = async () => {
    try {
      if (!accessToken) return;

      const response = await PromptsService.getRandomPrompt(accessToken);
      if (response.prompt) {
        setCurrentPrompt(response.prompt);
      }
    } catch (err) {
      console.error('Error fetching prompt:', err);
      // Don't show error to user, prompts are optional
    }
  };

  const initializeCall = async () => {
    try {
      setConnectionState('initializing');

      // Load user preferences for audio/video defaults
      let audioEnabled = true;
      let videoEnabled = true;
      try {
        const preferences = await usersService.getPreferences();
        audioEnabled = preferences.defaultAudioEnabled;
        videoEnabled = preferences.defaultVideoEnabled;
        console.log('Loaded preferences:', { audioEnabled, videoEnabled });
      } catch (err) {
        console.warn('Failed to load preferences, using defaults:', err);
      }

      // Create WebRTC service
      webrtcService.current = new WebRTCService(accessToken!);

      // Set up callbacks
      webrtcService.current.setCallbacks({
        onLocalStream: (stream) => {
          console.log('Local stream received');
          setLocalStream(stream);
        },
        onRemoteStream: (stream) => {
          console.log('Remote stream received');
          setRemoteStream(stream);
          setConnectionState('connected');
        },
        onConnectionStateChange: (state) => {
          console.log('Connection state changed:', state);
          setConnectionState(state);
        },
        onError: (err) => {
          console.error('WebRTC error:', err);
          setError(err.message);
          setConnectionState('error');

          // Check if error is related to session limit
          if (err.message && err.message.includes('Daily session limit')) {
            setUpgradeReason('daily_limit');
            setIsUpgradeModalOpen(true);
          }
        },
        onMatchFound: (data) => {
          console.log('Match found:', data);
          setPartnerId(data.partnerId);
          setRoomId(data.roomId);
          setIsSearching(false);
          setConnectionState('connecting');
        },
        onCallEnded: (reason) => {
          console.log('Call ended:', reason);
          handleCallEnd(reason);
        },
        onPartnerDisconnected: () => {
          console.log('Partner disconnected');
          handleCallEnd('Partner disconnected');
        },
      });

      // Connect to WebSocket server
      webrtcService.current.connect();

      // Wait a bit for connection
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Request ICE servers
      await webrtcService.current.requestICEServers();

      // Get local media stream with user's default preferences
      const stream = await webrtcService.current.getLocalStream(videoEnabled, audioEnabled);
      setLocalStream(stream);

      // Set initial audio/video states based on preferences
      setIsAudioEnabled(audioEnabled);
      setIsVideoEnabled(videoEnabled);

      setConnectionState('ready');
    } catch (err: any) {
      console.error('Initialization error:', err);
      setError(err.message || 'Failed to initialize video call');
      setConnectionState('error');
    }
  };

  const startSearching = () => {
    if (webrtcService.current && connectionState === 'ready') {
      setIsSearching(true);
      setConnectionState('searching');
      webrtcService.current.findMatch();
    }
  };

  const cancelSearch = () => {
    if (webrtcService.current) {
      webrtcService.current.cancelMatch();
      webrtcService.current.disconnect();
    }
    navigate('/');
  };

  const toggleAudio = () => {
    if (webrtcService.current) {
      const newState = !isAudioEnabled;
      webrtcService.current.toggleAudio(newState);
      setIsAudioEnabled(newState);
    }
  };

  const toggleVideo = () => {
    if (webrtcService.current) {
      const newState = !isVideoEnabled;
      webrtcService.current.toggleVideo(newState);
      setIsVideoEnabled(newState);
    }
  };

  const endCall = () => {
    if (webrtcService.current) {
      webrtcService.current.endCall('User ended call');
    }
    handleCallEnd('You ended the call');
  };

  const handleCallEnd = (reason: string) => {
    // Show rating modal if there was a completed session
    if (roomId && sessionTime > 0) {
      setIsRatingModalOpen(true);
    }

    setRemoteStream(null);
    setConnectionState('ready');
    console.log('Call ended:', reason);
  };

  const handleExit = () => {
    if (webrtcService.current) {
      webrtcService.current.disconnect();
    }
    navigate('/');
  };

  // Prompt handlers
  const handlePromptSkip = () => {
    setCurrentPrompt(null);
    console.log('User skipped prompt');
  };

  const handlePromptEngage = () => {
    setPromptEngaged(true);
    console.log('User engaged with prompt');
    // TODO: Track engagement in analytics
  };

  // Report handlers
  const handleOpenReport = () => {
    setIsReportModalOpen(true);
  };

  const handleCloseReport = () => {
    setIsReportModalOpen(false);
  };

  const handleSubmitReport = async (reason: string) => {
    if (!partnerId || !accessToken) {
      throw new Error('Cannot submit report: missing partner ID or access token');
    }

    try {
      await ReportsService.createReport(accessToken, partnerId, reason);
      console.log('Report submitted successfully');
    } catch (err) {
      console.error('Error submitting report:', err);
      throw err;
    }
  };

  const handleReportAndEndCall = () => {
    endCall();
  };

  // Next/Skip handler (premium only)
  const handleNext = () => {
    if (!isPremium) {
      setUpgradeReason('premium_feature');
      setIsUpgradeModalOpen(true);
      return;
    }

    if (webrtcService.current) {
      webrtcService.current.endCall('User skipped to next');
    }
    // Reset state and search for new match
    setRemoteStream(null);
    setCurrentPrompt(null);
    setPartnerId(null);
    setRoomId(null);
    setSessionTime(0);
    setSessionStartTime(null);

    // Start searching again immediately
    setTimeout(() => {
      if (connectionState === 'ready' || connectionState === 'connected') {
        startSearching();
      }
    }, 500);
  };

  // Camera switch handler (mobile)
  const handleSwitchCamera = async () => {
    if (webrtcService.current && localStream) {
      try {
        const videoTrack = localStream.getVideoTracks()[0];
        if (videoTrack) {
          // @ts-ignore - facingMode is not in all browsers
          const currentFacingMode = videoTrack.getSettings().facingMode || 'user';
          const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';

          // Stop current track
          videoTrack.stop();

          // Get new stream with different facing mode
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: newFacingMode },
            audio: true,
          });

          // Replace track in peer connection
          const videoSender = webrtcService.current.getPeerConnection()?.getSenders()
            .find(sender => sender.track?.kind === 'video');

          if (videoSender && newStream.getVideoTracks()[0]) {
            await videoSender.replaceTrack(newStream.getVideoTracks()[0]);
          }

          // Update local stream
          setLocalStream(newStream);
        }
      } catch (err) {
        console.error('Failed to switch camera:', err);
        alert('Failed to switch camera. This feature may not be supported on your device.');
      }
    }
  };

  // Rating modal handlers
  const handleCloseRatingModal = async () => {
    setIsRatingModalOpen(false);

    // Check if auto-connect after rating is enabled
    try {
      const preferences = await usersService.getPreferences();
      if (preferences.autoConnectAfterRating) {
        console.log('Auto-connecting to new match (preference enabled)');
        // Reset session data
        setRoomId(null);
        setPartnerId(null);
        setRemoteStream(null);
        setCurrentPrompt(null);
        setSessionTime(0);
        setSessionStartTime(null);

        // Navigate back to video page with auto-match
        navigate('/video', {
          state: {
            startMatching: true
          }
        });
        return;
      }
    } catch (err) {
      console.error('Failed to check auto-connect preference:', err);
    }

    // Reset session data (normal flow without auto-connect)
    setRoomId(null);
    setPartnerId(null);
  };

  const handleSubmitRating = async (rating: number, feedback?: string) => {
    if (!roomId || !accessToken) {
      throw new Error('Cannot submit rating: missing room ID or access token');
    }

    try {
      await SessionsService.rateSessionByRoomId(roomId, { rating, feedback }, accessToken);
      console.log('Rating submitted successfully');
    } catch (err) {
      console.error('Error submitting rating:', err);
      throw err;
    }
  };

  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
  };

  return (
    <div style={styles.container}>
      {/* Main video area */}
      <div style={styles.videoContainer}>
        {/* Remote video (large) */}
        <div style={styles.remoteVideo}>
          <VideoPlayer stream={remoteStream} label={remoteStream ? 'Partner' : undefined} />
        </div>

        {/* Local video (small, picture-in-picture) */}
        <div style={styles.localVideo}>
          <VideoPlayer stream={localStream} muted mirrored label="You" />
        </div>

        {/* Connection quality indicator */}
        {connectionState === 'connected' && connectionStats && (
          <div style={styles.qualityIndicator}>
            <div style={styles.qualityHeader}>
              <span style={styles.qualityIcon}>
                {qualityService.current.getQualityInfo(connectionStats.quality).icon}
              </span>
              <span
                style={{
                  ...styles.qualityLabel,
                  color: qualityService.current.getQualityInfo(connectionStats.quality).color,
                }}
              >
                {qualityService.current.getQualityInfo(connectionStats.quality).label}
              </span>
            </div>
            <div style={styles.qualityStats}>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Bitrate:</span>
                <span style={styles.statValue}>{connectionStats.bitrate} kbps</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Latency:</span>
                <span style={styles.statValue}>{connectionStats.roundTripTime} ms</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Packet Loss:</span>
                <span style={styles.statValue}>
                  {connectionStats.packetsReceived > 0
                    ? ((connectionStats.packetsLost / (connectionStats.packetsLost + connectionStats.packetsReceived)) * 100).toFixed(1)
                    : '0.0'}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Conversation Prompt */}
        {connectionState === 'connected' && currentPrompt && (
          <ConversationPrompt
            prompt={currentPrompt}
            onSkip={handlePromptSkip}
            onEngage={handlePromptEngage}
          />
        )}

        {/* Prompt Button - New conversation starter feature */}
        {connectionState === 'connected' && roomId && accessToken && webrtcService.current?.getSocket() && (
          <PromptButton
            socket={webrtcService.current.getSocket()!}
            roomId={roomId}
            accessToken={accessToken}
          />
        )}

        {/* Status overlay */}
        {connectionState !== 'connected' && (
          <div style={styles.statusOverlay}>
            <div style={styles.statusCard}>
              {connectionState === 'initializing' && (
                <>
                  <div style={styles.spinner} />
                  <p>Initializing...</p>
                </>
              )}
              {connectionState === 'ready' && !isSearching && (
                <>
                  <h2 style={styles.statusTitle}>Ready to connect</h2>
                  <p style={styles.statusText}>Click "Find Match" to start</p>
                  <button onClick={startSearching} style={styles.primaryButton}>
                    Find Match
                  </button>
                </>
              )}
              {connectionState === 'searching' && (
                <>
                  <div style={styles.spinner} />
                  <h2 style={styles.statusTitle}>Finding your match...</h2>
                  <p style={styles.statusText}>
                    Looking for someone to talk to...
                  </p>
                  <div style={styles.searchingTips}>
                    <p style={styles.tipText}>💡 Make sure your camera and mic are working</p>
                    <p style={styles.tipText}>🎭 Be yourself and have fun!</p>
                  </div>
                  <button onClick={cancelSearch} style={styles.secondaryButton}>
                    Cancel
                  </button>
                </>
              )}
              {connectionState === 'connecting' && (
                <>
                  <div style={styles.spinner} />
                  <p>Connecting to your match...</p>
                </>
              )}
              {connectionState === 'error' && (
                <>
                  <p style={styles.errorText}>{error || 'An error occurred'}</p>
                  <button onClick={handleExit} style={styles.secondaryButton}>
                    Go Back
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Call Controls */}
      {connectionState !== 'initializing' && connectionState !== 'searching' && (
        <CallControls
          isAudioEnabled={isAudioEnabled}
          isVideoEnabled={isVideoEnabled}
          onToggleAudio={toggleAudio}
          onToggleVideo={toggleVideo}
          onSwitchCamera={handleSwitchCamera}
          onNext={handleNext}
          onEndCall={connectionState === 'connected' ? endCall : handleExit}
          onReport={handleOpenReport}
          isPremium={isPremium}
          sessionTime={sessionTime}
          timeRemaining={!isPremium ? FREE_SESSION_LIMIT - sessionTime : undefined}
          connectionState={connectionState}
          connectionQuality={
            connectionStats
              ? qualityService.current.getQualityInfo(connectionStats.quality)
              : undefined
          }
          disabled={connectionState === 'error'}
        />
      )}

      {/* Report Modal */}
      {partnerId && (
        <ReportModal
          isOpen={isReportModalOpen}
          partnerId={partnerId}
          onClose={handleCloseReport}
          onSubmit={handleSubmitReport}
          onEndCall={handleReportAndEndCall}
        />
      )}

      {/* Rating Modal */}
      {isRatingModalOpen && (
        <RatingModal
          roomId={roomId}
          accessToken={accessToken}
          onClose={handleCloseRatingModal}
          sessionDuration={sessionTime}
          promptUsed={promptEngaged}
        />
      )}

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={isUpgradeModalOpen}
        onClose={handleCloseUpgradeModal}
        reason={upgradeReason}
        sessionsUsedToday={sessionsUsedToday}
        dailyLimit={dailyLimit}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: '#0D1B2A',
  },
  videoContainer: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  remoteVideo: {
    width: '100%',
    height: '100%',
  },
  localVideo: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    width: '240px',
    height: '180px',
    border: '2px solid white',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    zIndex: 10,
  },
  qualityIndicator: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    borderRadius: 'var(--radius-md)',
    padding: '0.75rem 1rem',
    color: 'white',
    fontSize: '0.9rem',
    zIndex: 10,
    minWidth: '200px',
  },
  qualityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
  },
  qualityIcon: {
    fontSize: '1.2rem',
  },
  qualityLabel: {
    fontWeight: '600',
    fontSize: '1rem',
  },
  qualityStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
  },
  statLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  statValue: {
    color: 'white',
    fontWeight: '500',
  },
  statusOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 5,
  },
  statusCard: {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    textAlign: 'center',
    minWidth: '300px',
  },
  statusTitle: {
    color: 'var(--color-deep-purple)',
    marginBottom: '0.5rem',
  },
  statusText: {
    color: 'var(--color-gray-600)',
    marginBottom: '1.5rem',
  },
  errorText: {
    color: '#c33',
    marginBottom: '1rem',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid var(--color-gray-200)',
    borderTop: '4px solid var(--color-warm-orange)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  searchingTips: {
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
  },
  tipText: {
    margin: '0.5rem 0',
    fontSize: '0.9rem',
    color: 'var(--color-gray-600)',
  },
  primaryButton: {
    padding: '0.75rem 2rem',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '0.75rem 2rem',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-gray-300)',
    backgroundColor: 'white',
    color: 'var(--color-dark-navy)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
